import { Star } from 'lucide-react';
import CTAButton from '../components/CTAButton';

export default function ReviewsPage() {
  const testimonials = [
    {
      name: 'Brenda C.',
      city: 'Weirton, WV',
      situation: 'Downsizing Home',
      rating: 5,
      quote: "They were great. They understood how I needed to sell my home. They were there to answer any question you might have. It was a great experience."
    },
    {
      name: 'Robin M.',
      city: 'Vicksburg, MS',
      situation: 'Mortgage Issues',
      rating: 5,
      quote: "Very knowledgeable. They reviewed my particular situation and provided immediate guidance — free of charge — that got me where I needed to be."
    },
    {
      name: 'Jill A.',
      city: 'San Antonio, TX',
      situation: 'Inherited Property',
      rating: 5,
      quote: "If I ever needed to sell a property quickly, I'd feel comfortable trusting them with the process."
    },
    {
      name: 'Connor S.',
      city: 'Louisville, KY',
      situation: 'Major Repairs Needed',
      rating: 5,
      quote: "They don't overpromise, and they're upfront about what works and what doesn't."
    },
    {
      name: 'Brandon E.',
      city: 'San Antonio, TX',
      situation: 'Tired Landlord',
      rating: 5,
      quote: "They're patient, respectful, and actually take the time to walk people through their options."
    },
    {
      name: 'Joe S.',
      city: 'Louisville, KY',
      situation: 'Listed but Didn\'t Sell',
      rating: 5,
      quote: "From everything I've seen, they prioritize clarity and fairness over pressure."
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">What People Say About Quick Close</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Feedback from people familiar with our approach, communication, and professionalism.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.city}</div>
                    <div className="text-xs text-blue-900 font-semibold mt-1">{testimonial.situation}</div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Our Clients Trust Us</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="text-4xl font-bold text-blue-900 mb-2">100%</div>
              <p className="text-gray-600">No-Pressure Process</p>
              <p className="text-sm text-gray-500 mt-2">We never pressure sellers. It's your decision, your timeline.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="text-4xl font-bold text-blue-900 mb-2">Fair</div>
              <p className="text-gray-600">Offers Based on Market</p>
              <p className="text-sm text-gray-500 mt-2">We make offers based on real market analysis, not predatory tactics.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <div className="text-4xl font-bold text-blue-900 mb-2">100%</div>
              <p className="text-gray-600">Transparent & Honest</p>
              <p className="text-sm text-gray-500 mt-2">No hidden fees, no surprises, no fine print. What you see is what you get.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Common Situations We Help With</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: 'Facing Foreclosure', desc: 'We can help you avoid foreclosure and walk away with something in your pocket.' },
              { title: 'Inherited a House', desc: 'Don\'t want the landlord life? We can buy the property and handle everything.' },
              { title: 'Major Repairs Needed', desc: 'Sell as-is without the $20K-$50K price tag for repairs.' },
              { title: 'Divorce or Life Change', desc: 'Need a clean break? We offer a fast, simple sale.' },
              { title: 'Bad Tenants / Landlord Burnout', desc: 'We buy rental properties and take the headaches off your plate.' },
              { title: 'Listed But Didn\'t Sell', desc: 'If a traditional listing didn\'t work, we\'re here as a backup plan.' },
              { title: 'Job Relocation', desc: 'Moving fast? We can close quickly so you\'re ready for your next chapter.' },
              { title: 'Underwater on Mortgage', desc: 'In some situations, we can help you move forward even if you owe money.' }
            ].map((situation, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{situation.title}</h3>
                <p className="text-gray-600 text-sm">{situation.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Want to Share Your Story?</h2>
          <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center">
            <p className="text-lg text-gray-600 mb-6">
              If you've worked with Quick Close, we'd love to hear about your experience. Your review helps other sellers understand what to expect.
            </p>
            <a
              href="https://g.page/r/CY8eGPP7mjq2EAE/review"
              className="inline-block bg-blue-900 text-white px-8 py-3 rounded-lg hover:bg-blue-950 transition-colors font-semibold"
            >
              Leave a Review on Google
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-950">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Become Our Next Success Story?</h2>
          <p className="text-xl mb-8 text-blue-50 max-w-2xl mx-auto">
            Get started today and see why hundreds of homeowners have chosen Quick Close.
          </p>
          <CTAButton text="Get Your Offer" size="lg" variant="primary" />
        </div>
      </section>
    </div>
  );
}
